import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { KartenAblauf } from './KartenDialogV2';
import { kartenStatusSchritte, kartenStatusTasten, type KartenStatusArt } from './logik';
import { Kuerzel, ZahlungsHuelle } from './ZahlungsHuelle';

const UEBERSCHRIFT: Record<KartenStatusArt, string> = {
  start: 'Kartenzahlung wird gestartet …',
  beleg: 'Zahlung bestätigt – Beleg wird erstellt',
  abgelehnt: 'Zahlung abgelehnt',
  abgebrochen: 'Zahlung abgebrochen',
  start_unklar: 'Antwort zum Kartenzahlungsstart fehlt',
};

const ERKLAERUNG: Record<KartenStatusArt, string> = {
  start: 'Die Zahlung wird an das Terminal gesendet. Nicht erneut starten.',
  beleg: 'Das Terminal hat die Zahlung bestätigt. Der Beleg wird jetzt erstellt.',
  abgelehnt: 'Das Terminal hat abgelehnt. Es wurde kein Beleg erstellt.',
  abgebrochen: 'Die Kartenzahlung wurde abgebrochen. Es wurde kein Beleg erstellt.',
  start_unklar:
    'Die Zahlung kann am Terminal trotzdem laufen. Erst „Zahlung prüfen“ – kein neuer Start, keine Barzahlung für diesen Einkauf.',
};

/**
 * ZD-V2-F1 (nur mit Schalter `bilanx.zahlungsdialogV2`): Kartenzustände, die `Verkauf.tsx` vor
 * und nach dem Kartendialog führt (Figma FINAL-K1/K4/K6/K7), in der V2-Hülle. Reine Anzeige:
 * Der Zustand kommt aus dem Verkauf, die Tasten rufen dieselben Funktionen mit denselben
 * Bedingungen wie die Zahlleiste („Bar“, „Karte“, „Zahlung prüfen“). Keine Taste startet hier
 * etwas, was die Zahlleiste nicht ebenso starten könnte; bei `start`, `beleg` und
 * `start_unklar` gibt es gar keinen Kassierweg.
 */
export function KartenStatusV2(props: {
  art: KartenStatusArt;
  betragCent: number;
  info?: string | undefined;
  /** „Erneut mit Karte“ (nur abgelehnt/abgebrochen): sichtbar bzw. bedienbar wie „Karte“. */
  karte?: { sichtbar: boolean; bedienbar: boolean; onKlick: () => void };
  /** „Bar zahlen“ (nur abgelehnt/abgebrochen): sichtbar bzw. bedienbar wie „Bar“. */
  bar?: { sichtbar: boolean; bedienbar: boolean; onKlick: () => void };
  /** „Zahlung prüfen“ (nur start_unklar), wie in der Zahlleiste. */
  onPruefen?: () => void;
  /** Anzeige schließen – ändert nichts am Vorgang (Bon und Sperren bleiben). */
  onSchliessen?: () => void;
}) {
  const { art } = props;
  const regel = kartenStatusTasten(art);
  const ergebnis = art === 'abgelehnt' || art === 'abgebrochen';
  const ton = ergebnis
    ? 'gefahr'
    : art === 'beleg'
      ? 'erfolg'
      : art === 'start_unklar'
        ? 'warnung'
        : 'marke';

  return (
    <ZahlungsHuelle
      art="Kartenzahlung"
      betragLabel="Betrag Karte"
      betragCent={props.betragCent}
      info={props.info}
      regel={regel}
      beschaeftigt={art === 'start' || art === 'beleg'}
      klasse="zv2-karte zv2-kartenstatus"
      fokusSchluessel={art}
      onTaste={(e: ReactKeyboardEvent<HTMLDivElement>) => {
        // Esc schließt nur die Ergebnisanzeige (K6); sonst ist Esc gesperrt (Tastensperre)
        if (ergebnis && e.key === 'Escape' && !e.repeat) {
          props.onSchliessen?.();
        }
      }}
      fuss={
        ergebnis ? (
          <>
            {props.karte?.sichtbar === true && (
              <button
                type="button"
                className="taste gross zv2-primaer"
                disabled={!props.karte.bedienbar}
                aria-keyshortcuts="F3"
                onClick={props.karte.onKlick}
              >
                Erneut mit Karte <Kuerzel>F3</Kuerzel>
              </button>
            )}
            {props.bar?.sichtbar === true && (
              <button
                type="button"
                className="taste zweit"
                disabled={!props.bar.bedienbar}
                aria-keyshortcuts="F2"
                onClick={props.bar.onKlick}
              >
                Bar zahlen <Kuerzel>F2</Kuerzel>
              </button>
            )}
            <button
              type="button"
              className="taste zweit zv2-abbrechen"
              aria-keyshortcuts="Escape"
              onClick={props.onSchliessen}
            >
              Abbrechen <Kuerzel>Esc</Kuerzel>
            </button>
          </>
        ) : art === 'start_unklar' ? (
          <>
            <button
              type="button"
              className="taste zweit zv2-abbrechen"
              title="Die Kasse merkt sich den Start – „Zahlung prüfen“ bleibt in der Zahlleiste"
              onClick={props.onSchliessen}
            >
              Schließen – Zahlung bleibt offen
            </button>
            <button type="button" className="taste gross zv2-primaer" onClick={props.onPruefen}>
              Zahlung prüfen
            </button>
          </>
        ) : null
      }
    >
      <div className="zv2-links">
        <div className="zv2-karten-kopf">
          <span className={`zv2-symbol ${ton}`} aria-hidden="true">
            ▭
          </span>
          <h3 role="status" aria-live="polite">
            {UEBERSCHRIFT[art]}
          </h3>
        </div>
        <p className="zv2-text">{ERKLAERUNG[art]}</p>
        {ergebnis && (
          <div className="zv2-meldung gefahr" role="alert">
            <strong>Kein Beleg erstellt</strong>
            <span>Andere Karte oder Barzahlung wählen.</span>
          </div>
        )}
        {art === 'start_unklar' && (
          <div className="zv2-meldung warnung" role="alert">
            <strong>Kein Beleg, kein neuer Start</strong>
            <span>Erst „Zahlung prüfen“. Der Kassierweg bleibt bis zur Klärung gesperrt.</span>
          </div>
        )}
      </div>
      <KartenAblauf schritte={kartenStatusSchritte(art)} />
    </ZahlungsHuelle>
  );
}
